'use client';

import Breadcrumbs from '@/components/common/Breadcrumbs';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';
import CountUp from 'react-countup';

interface InvestmentHeroProps {
  className?: string;
}

const InvestmentHero = ({ className = '' }: InvestmentHeroProps) => {
  return (
    <section
      data-header="dark"
      className={`relative  bg-gradient-to-r from-green-900 to-black bg-[length:200%_200%] animate-gradient-x text-white ${className}`}
    >
      <div className="absolute inset-0 opacity-10">
        <AppImage
          src="https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=1920&q=80"
          alt="Modern office desk with financial charts and graphs displayed on laptop screen showing investment analysis"
          className="w-full h-full object-cover"
        />
      </div>
      <div className="relative container mx-auto px-4 py-4 pt-16 lg:pt-20 mb-2 sm:mb-8">
        <Breadcrumbs className="" />
        <div className="max-w-6xl">
          {/* Badge */}
          <div className="inline-flex items-center space-x-2 bg-white/10 backdrop-blur-sm px-4 py-1 sm:py-2 rounded-full mb-2 sm:mb-4 md:mb-6">
            <Icon name="SparklesIcon" size={18} variant="solid" className="text-accent" />
            <span className="font-body text-sm font-medium text-accent">
              Institutional Investment Framework
            </span>
          </div>

          {/* Heading */}
          <h1 className="font-headline text-3xl sm:text-5xl lg:text-6xl font-bold mb-2 sm:mb-4 md:mb-6 leading-tight">
            Disciplined Capital for Transformative Impact
          </h1>
          <p className="font-body text-lg lg:text-xl text-gray-200 leading-relaxed mb-2 sm:mb-4 md:mb-6 max-w-3xl">
            Our investment approach combines patient capital with systematic methodology, delivering
            measurable returns while building sustainable economic infrastructure across Africa.
            This is development finance reimagined for institutional sophistication.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 sm:gap-8 lg:gap-16 mb-6 md:mb-12">
            <div className="flex flex-col bg-white/10 backdrop-blur-sm p-2 sm:p-4 rounded-lg">
              <span className="font-headline text-2xl sm:text-3xl lg:text-4xl font-semibold text-accent">
                <CountUp end={15} duration={10} decimals={0} />% -{' '}
                <CountUp end={20} duration={10} decimals={0} />%
              </span>
              <span className="font-body text-xs sm:text-sm text-gray-300 mt-1">Target IRR</span>
            </div>

            <div className="flex flex-col bg-white/10 backdrop-blur-sm p-2 sm:p-4 rounded-lg">
              <span className="font-headline text-2xl sm:text-3xl lg:text-4xl font-semibold text-accent">
                <CountUp end={10} duration={10} decimals={0} /> -{' '}
                <CountUp end={15} duration={10} decimals={0} /> Years
              </span>
              <span className="font-body text-xs sm:text-sm text-gray-300 mt-1">
                Investment Horizon
              </span>
            </div>

            <div className="flex flex-col bg-white/10 backdrop-blur-sm p-2 sm:p-4 rounded-lg">
              <span className="font-headline text-2xl sm:text-3xl lg:text-4xl font-semibold text-accent">
                $<CountUp end={500} duration={10} decimals={0} />M - $
                <CountUp end={2} duration={10} decimals={0} />B
              </span>
              <span className="font-body text-xs sm:text-sm text-gray-300 mt-1">
                Target Fund Size
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default InvestmentHero;
